import Link from "next/link";
import type { ReactNode } from "react";
import { TwtBrand, TwtBrandMark } from "~/components/TwtBrand";
import { NewsletterSignupLink } from "~/components/newsletter/NewsletterSignupLink";

import { blogHref, launchUnavailableHref, publicNavLinks } from "~/components/public-nav-links";

export { publicNavLinks };

const footerLinks = [
  { label: "Approach", href: "/approach" },
  { label: "Pricing", href: "/pricing" },
  { label: "Blog", href: blogHref },
  { label: "Newsletter", href: launchUnavailableHref },
  { label: "Discord", href: "https://discord.com/invite/tradewithtitans" },
  { label: "Contact", href: "/contact-us" },
  { label: "Terms", href: "/terms" },
];

function isExternalHref(href: string) {
  return href.startsWith("http");
}

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

function MarketingLink({
  href,
  className,
  onClick,
  children,
}: {
  href: string;
  className?: string;
  onClick?: () => void;
  children: ReactNode;
}) {
  if (isExternalHref(href)) {
    return (
      <a href={href} className={className} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <Link href={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

function MarketingBrandLogo({ className = "" }: { className?: string }) {
  return (
    <TwtBrand
      size="md"
      className={cx("gap-2 sm:gap-2.5", className)}
      markClassName="h-6 w-6 text-[14px] sm:h-7 sm:w-7 sm:text-[15px]"
      textClassName="whitespace-nowrap text-[16px] sm:text-[20px] md:text-[22px]"
    />
  );
}

export function MarketingMark({ size = 34 }: { size?: number }) {
  return (
    <TwtBrandMark
      size="lg"
      className="shrink-0"
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(6, Math.round(size * 0.22)),
        fontSize: Math.round(size * 0.58),
      }}
    />
  );
}

export { MarketingNav } from "./MarketingNav";

export function MarketingFooter() {
  return (
    <footer className="border-t border-[#d7cfbf] bg-[#f6f3ea] px-6 py-12 text-[#14120f] md:px-10">
      <div className="mx-auto flex max-w-[1360px] flex-col gap-8 md:flex-row md:items-start md:justify-between">
        <div>
          <Link
            href="/"
            className="flex items-center text-[#14120f]"
            aria-label="Trade with Titans home"
          >
            <MarketingBrandLogo />
          </Link>
          <p className="mt-4 max-w-xs text-[15px] leading-6 text-[#6a5d4c] [overflow-wrap:anywhere]">
            Long-context AI agents for financial markets.
          </p>
        </div>
        <nav className="flex flex-wrap gap-x-6 gap-y-3 text-[15px] font-medium text-[#4f463a]">
          {footerLinks.map((link) =>
            link.label === "Newsletter" ? (
              <NewsletterSignupLink
                key={link.label}
                source="footer"
                className="cursor-pointer bg-transparent text-left transition hover:text-[#14120f]"
              >
                Newsletter
              </NewsletterSignupLink>
            ) : (
              <MarketingLink
                key={`${link.label}:${link.href}`}
                href={link.href}
                className="transition hover:text-[#14120f]"
              >
                {link.label}
              </MarketingLink>
            )
          )}
        </nav>
      </div>
    </footer>
  );
}

export const publicPageShellClassName =
  "min-h-screen bg-[#f6f3ea] text-[#14120f] [font-family:var(--twt-public-sans)]";

export const publicContainerClassName = "mx-auto max-w-[1360px]";

export function MarketingKicker({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        "inline-flex items-center gap-3 text-[13px] font-semibold leading-5 text-[#9a4d2c]",
        className
      )}
    >
      <span className="h-px w-10 bg-[#d97757]" />
      {children}
    </div>
  );
}

export function MarketingHeroTitle({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <h1
      className={cx(
        "mt-8 max-w-[980px] text-[56px] font-normal leading-[0.94] tracking-normal text-[#14120f] [font-family:var(--twt-public-serif),Georgia,serif] md:text-[84px] xl:text-[96px]",
        className
      )}
    >
      {children}
    </h1>
  );
}

export function MarketingLead({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      className={cx(
        "mt-8 max-w-[760px] text-[20px] font-medium leading-8 text-[#51483b] md:text-[23px] md:leading-9",
        className
      )}
    >
      {children}
    </p>
  );
}
